import { useTranslation } from 'react-i18next';

import type { Task, TaskSize } from '../../../api/types';
import { notifyError, notifySuccess } from '../../../shared/lib/haptics';
import { Button } from '../../../shared/ui/Button';
import { TaskGroupSection } from '../components/TaskGroupSection';
import { WorkBanner } from '../components/WorkBanner';
import { useChangeTaskStatus } from '../hooks/useChangeTaskStatus';
import { useTasks } from '../hooks/useTasks';
import { nextStatusForTap } from '../lib/taskStatusToggle';
import { isTaskNotFound } from '../lib/updateTaskError';
import styles from './BacklogScreen.module.css';

interface BacklogScreenProps {
  onOpenTask: (taskId: number) => void;
  onProcrastinate: () => void;
  onCreateTask: () => void;
  // PARA-135 (AC-6, AC-8) — push({ type: 'edit-task', taskId }) делает AppShell.
  onEditTask: (taskId: number) => void;
}

const SIZES: TaskSize[] = ['SMALL', 'MEDIUM', 'BIG'];

function groupBySize(tasks: Task[]): Record<TaskSize, Task[]> {
  const grouped: Record<TaskSize, Task[]> = { SMALL: [], MEDIUM: [], BIG: [] };
  for (const task of tasks) {
    grouped[task.size].push(task);
  }
  return grouped;
}

// PARA-105, AC-2/AC-8/AC-9 — главный экран: баннер, секции по размеру.
// PARA-122 (D6, AC-6): CTA «Мне лень» — Button variant="soft" внутри WorkBanner
// (слот children), больше не raw <button>. Контент — в телефонном контейнере.
// PARA-135 (AC-1/AC-4/AC-5): короткий тап по телу карточки меняет статус.
// Группировка, сортировка и состав списка не меняются — DONE и CANCELLED
// остаются видимыми, фильтрации по статусу нет (РЕШ-7, AC-5a).
export function BacklogScreen({
  onOpenTask,
  onProcrastinate,
  onCreateTask,
  onEditTask,
}: BacklogScreenProps): JSX.Element {
  const { t } = useTranslation('tasks');
  const { data } = useTasks();
  const { mutate, isPending, isError, error, variables } = useChangeTaskStatus();
  const tasks = data ?? [];
  const grouped = groupBySize(tasks);

  const handleTakeIntoWork = (task: Task): void => {
    // AC-5: терминальные статусы — no-op: ни запроса, ни отдачи, ни ошибки.
    const next = nextStatusForTap(task.status);
    if (next === null) {
      return;
    }
    // AC-14: защита от дублей — по ТОЙ ЖЕ карточке. Глобальный isPending
    // заблокировал бы весь список.
    if (isPending && variables?.taskId === task.id) {
      return;
    }
    mutate(
      { taskId: task.id, status: next },
      { onSuccess: () => notifySuccess(), onError: () => notifyError() },
    );
  };

  // AC-E3 (H-2): задача удалена в другой сессии — сообщение про «не найдена»,
  // а не общее «не удалось сменить статус». Кэш к этому моменту уже
  // инвалидирован в onSettled, поэтому карточка исчезает сама.
  const statusErrorMessage = isTaskNotFound(error)
    ? t('form.errors.notFound')
    : t('errors.statusChangeFailed');

  return (
    <div className={styles.screen}>
      <WorkBanner subtitle={t('backlogSubtitle', { count: tasks.length })}>
        <Button variant="soft" fullWidth onClick={onProcrastinate}>
          {t('procrastinationButton')}
        </Button>
      </WorkBanner>
      <div className={styles.createCta}>
        <Button variant="primary" fullWidth onClick={onCreateTask}>
          + {t('createTask')}
        </Button>
      </div>
      {isError && <p className={styles.statusError}>{statusErrorMessage}</p>}
      {SIZES.map((size) => (
        <TaskGroupSection
          key={size}
          size={size}
          tasks={grouped[size]}
          onOpenTask={onOpenTask}
          onTakeIntoWork={handleTakeIntoWork}
          onEditTask={onEditTask}
        />
      ))}
    </div>
  );
}
